import { VerifyPayeeCheckResponseCode } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { ComponentProps, useState } from 'react';
import { BankAccountDetailsConfirmation } from './bank-account-details-confirmation';
import { BankAccountDetailsForm, BankAccountDetailsFormValues } from './bank-account-details-form';

type EmployeeEditStep = 'PERSONAL_INFORMATION' | 'CONFIRMATION';

type EditEmployeeDetailsFlowProps = {
  employeeDetails: ComponentProps<typeof BankAccountDetailsForm>['initialValues'];
  matchedName?: string;
  isCurrencyGbp: boolean;
  isVerifyingPayee: boolean;
  payeeCheckCode?: VerifyPayeeCheckResponseCode;
  onEditEmployee: (data: BankAccountDetailsFormValues) => Promise<void>;
  onFinalizeFlow: () => void;
};

export const EditEmployeeDetailsFlow = ({
  employeeDetails,
  matchedName,
  isCurrencyGbp,
  isVerifyingPayee,
  payeeCheckCode,
  onEditEmployee,
  onFinalizeFlow,
}: EditEmployeeDetailsFlowProps) => {
  const [step, setStep] = useState<EmployeeEditStep>('PERSONAL_INFORMATION');

  switch (step) {
    case 'PERSONAL_INFORMATION': {
      const baseConfig = {
        initialValues: employeeDetails,
        isCurrencyGbp,
      };

      if (isVerifyingPayee) {
        return <BankAccountDetailsForm {...baseConfig} payeeCheckCompletionConfig={{ status: 'LOADING' as const }} />;
      }

      const handleEditEmployeeSubmit = async (data: BankAccountDetailsFormValues) => {
        if (payeeCheckCode === VerifyPayeeCheckResponseCode.CLOSE_MATCH) {
          if (!matchedName) {
            Sentry.captureMessage('No matched name found when calling onEditEmployee in EditEmployeeDetailsFlow', {
              extra: {
                payeeCheckCode,
                data,
              },
            });

            return;
          }

          const [firstName, ...rest] = matchedName.trim().split(/\s+/);
          const lastName = rest.length > 0 ? rest.join(' ') : '';

          await onEditEmployee({ ...data, fname: firstName, lname: lastName });

          return;
        }

        return onEditEmployee(data);
      };

      const handleContinueToConfirmation = () => {
        setStep('CONFIRMATION');
      };

      const payeeCheckConfig = getPayeeCheckConfig({
        payeeCheckCode,
        matchedName,
        onSubmit: handleEditEmployeeSubmit,
        onContinue: handleContinueToConfirmation,
      });

      return <BankAccountDetailsForm {...baseConfig} payeeCheckCompletionConfig={payeeCheckConfig} />;
    }
    case 'CONFIRMATION':
      return (
        <BankAccountDetailsConfirmation
          onEditDetails={() => setStep('PERSONAL_INFORMATION')}
          onContinue={onFinalizeFlow}
        />
      );
  }
};

type PayeeCheckConfigArgs = {
  payeeCheckCode?: VerifyPayeeCheckResponseCode;
  matchedName?: string;
  onSubmit: (data: BankAccountDetailsFormValues) => Promise<void>;
  onContinue: () => void;
};

const getPayeeCheckConfig = ({
  payeeCheckCode,
  matchedName,
  onSubmit,
  onContinue,
}: PayeeCheckConfigArgs): ComponentProps<typeof BankAccountDetailsForm>['payeeCheckCompletionConfig'] => {
  if (!payeeCheckCode) {
    return {
      status: 'IDLE' as const,
      onSubmit,
    };
  }

  if (payeeCheckCode === VerifyPayeeCheckResponseCode.MATCHED) {
    return {
      status: 'COMPLETED' as const,
      completedCheckConfig: {
        completionStatus: 'MATCHED' as const,
        code: payeeCheckCode,
        onSubmit,
      },
    };
  }

  if (payeeCheckCode === VerifyPayeeCheckResponseCode.CLOSE_MATCH) {
    return {
      status: 'COMPLETED' as const,
      completedCheckConfig: {
        completionStatus: 'CLOSE_MATCH' as const,
        code: payeeCheckCode,
        matchedName,
        onSubmit,
        onContinue,
      },
    };
  }

  return {
    status: 'COMPLETED' as const,
    completedCheckConfig: {
      completionStatus: 'GENERAL' as const,
      code: payeeCheckCode,
      onSubmit,
      onContinue,
    },
  };
};
